import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { REGISTRATION_AGREEMENT_VERSION } from '../constants/registrationAgreement';
import { hasPermission } from '../constants/roles';

const AuthContext = createContext(null);

async function fetchProfile(userId) {
  const { data, error } = await supabase
    .from('gfi_profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canManageTraining, setCanManageTraining] = useState(false);
  const [canUploadResources, setCanUploadResources] = useState(false);

  const refreshTrainingPermission = useCallback(async () => {
    const { data, error } = await supabase.rpc('gfi_can_manage_training');
    if (error) {
      console.error(error);
      setCanManageTraining(false);
      return false;
    }
    const allowed = Boolean(data);
    setCanManageTraining(allowed);
    return allowed;
  }, []);

  const refreshUploadResourcesPermission = useCallback(async () => {
    const { data, error } = await supabase.rpc('gfi_can_upload_resources');
    if (error) {
      console.error(error);
      setCanUploadResources(false);
      return false;
    }
    const allowed = Boolean(data);
    setCanUploadResources(allowed);
    return allowed;
  }, []);

  const loadProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      return null;
    }
    const data = await fetchProfile(userId);
    setProfile(data);
    return data;
  }, []);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!mounted) return;
      setSession(initialSession);
      if (initialSession?.user) {
        Promise.all([
          loadProfile(initialSession.user.id),
          refreshTrainingPermission(),
          refreshUploadResourcesPermission(),
        ]).finally(() => {
          if (mounted) setLoading(false);
        });
      } else {
        setCanManageTraining(false);
        setCanUploadResources(false);
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (nextSession?.user) {
        loadProfile(nextSession.user.id);
        refreshTrainingPermission();
        refreshUploadResourcesPermission();
      } else {
        setProfile(null);
        setCanManageTraining(false);
        setCanUploadResources(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [loadProfile, refreshTrainingPermission]);

  const signIn = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUp = useCallback(async ({ fullName, email, password, agentCode, agreementAccepted }) => {
    if (!agreementAccepted) {
      throw new Error('Debes aceptar el acuerdo de registro para continuar.');
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName.trim(),
          agent_code: agentCode.trim(),
          registration_agreement_version: REGISTRATION_AGREEMENT_VERSION,
        },
      },
    });
    if (error) throw error;
    return data;
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setSession(null);
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    const redirectTo = `${window.location.origin}/restablecer-contrasena`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (password) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!session?.user?.id) return null;
    return loadProfile(session.user.id);
  }, [loadProfile, session?.user?.id]);

  const updateOwnProfile = useCallback(async (updates) => {
    if (!session?.user?.id) throw new Error('No autenticado');
    const { data, error } = await supabase
      .from('gfi_profiles')
      .update(updates)
      .eq('id', session.user.id)
      .select()
      .single();
    if (error) throw error;
    setProfile(data);
    return data;
  }, [session?.user?.id]);

  const setUserRole = useCallback(async (targetUserId, newRole) => {
    const { data, error } = await supabase.rpc('gfi_superadmin_set_role', {
      target_user_id: targetUserId,
      new_role: newRole,
    });
    if (error) throw error;
    return data;
  }, []);

  const resolveAgentCode = useCallback(async (targetUserId, newAgentCode) => {
    const { data, error } = await supabase.rpc('gfi_admin_resolve_agent_code', {
      target_user_id: targetUserId,
      new_agent_code: newAgentCode,
    });
    if (error) throw error;
    return data;
  }, []);

  const value = useMemo(() => {
    const role = profile?.role ?? null;
    return {
      session,
      user: session?.user ?? null,
      profile,
      role,
      loading,
      isAuthenticated: Boolean(session?.user),
      signIn,
      signUp,
      signOut,
      requestPasswordReset,
      updatePassword,
      refreshProfile,
      updateOwnProfile,
      setUserRole,
      resolveAgentCode,
      can: (permission) => hasPermission(role, permission),
      canManageTraining,
      refreshTrainingPermission,
      canUploadResources,
      refreshUploadResourcesPermission,
    };
  }, [
    session,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    requestPasswordReset,
    updatePassword,
    refreshProfile,
    updateOwnProfile,
    setUserRole,
    resolveAgentCode,
    canManageTraining,
    refreshTrainingPermission,
    canUploadResources,
    refreshUploadResourcesPermission,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
